<template>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow">
        <div class="modal-header">
          <div>
            <h5 class="modal-title mb-1">Import Members</h5>
            <div class="text-muted small">
              Upload an Excel or CSV file to create members with opening balance and commitment.
            </div>
          </div>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <div v-if="error" class="alert alert-danger py-2">
            {{ error }}
          </div>

          <div v-if="successMessage" class="alert alert-success py-2">
            {{ successMessage }}
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Download Template</label>
            <div>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="downloadingTemplate"
                @click="downloadTemplate"
              >
                <i class="bi bi-download me-1"></i>
                {{ downloadingTemplate ? "Downloading..." : "Download Template" }}
              </button>
            </div>
            <div class="form-text">
              Use the official template to avoid wrong column names.
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Select File</label>
            <input
              ref="fileInput"
              type="file"
              class="form-control"
              accept=".xlsx,.xls,.csv"
              @change="onFileChange"
            />
            <div class="form-text">
              Accepted formats: .xlsx, .xls, .csv
            </div>
          </div>

          <div v-if="fileName" class="mb-3">
            <span class="badge text-bg-light border">
              <i class="bi bi-file-earmark-spreadsheet me-1"></i>{{ fileName }}
            </span>
          </div>

          <div v-if="result" class="mt-4">
            <h6 class="fw-bold mb-3">Import Result</h6>

            <div class="row g-2 mb-3">
              <div class="col-md-4">
                <div class="border rounded p-3 bg-light">
                  <div class="small text-muted">Total Rows</div>
                  <div class="fw-bold fs-5">{{ result.total_rows || 0 }}</div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="border rounded p-3 bg-success-subtle">
                  <div class="small text-muted">Successful</div>
                  <div class="fw-bold fs-5 text-success">{{ result.success_count || 0 }}</div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="border rounded p-3 bg-danger-subtle">
                  <div class="small text-muted">Failed</div>
                  <div class="fw-bold fs-5 text-danger">{{ result.failed_count || 0 }}</div>
                </div>
              </div>
            </div>

            <div v-if="result.failures?.length" class="mb-3">
              <h6 class="text-danger">Failed Rows</h6>
              <div class="table-responsive border rounded">
                <table class="table table-sm mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in result.failures" :key="item.row">
                      <td>{{ item.row }}</td>
                      <td>{{ item.name || "-" }}</td>
                      <td>{{ item.email || "-" }}</td>
                      <td>{{ item.phone || "-" }}</td>
                      <td class="text-danger">{{ item.error }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="result.successes?.length" class="mb-2">
              <h6 class="text-success">Successful Rows</h6>
              <div class="table-responsive border rounded">
                <table class="table table-sm mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in result.successes" :key="item.row">
                      <td>{{ item.row }}</td>
                      <td>{{ item.name || "-" }}</td>
                      <td>{{ item.email || "-" }}</td>
                      <td>{{ item.phone || "-" }}</td>
                      <td>{{ item.generated_password || "-" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="submitting"
            @click="$emit('close')"
          >
            Close
          </button>

          <button
            type="button"
            class="btn btn-success"
            :disabled="submitting || !file"
            @click="submit"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bi bi-upload me-2"></i>
            {{ submitting ? "Importing..." : "Import Members" }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-backdrop fade show"></div>
</template>

<script>
import { importMembers, downloadMembersTemplate } from "@/services/members";

export default {
  name: "ImportMembersModal",
  emits: ["close", "imported"],

  data() {
    return {
      file: null,
      fileName: "",
      submitting: false,
      downloadingTemplate: false,
      error: "",
      successMessage: "",
      result: null,
    };
  },

  methods: {
    onFileChange(e) {
      const selected = e.target.files?.[0] || null;
      this.file = selected;
      this.fileName = selected ? selected.name : "";
      this.error = "";
      this.successMessage = "";
      this.result = null;
    },

    async downloadTemplate() {
      this.downloadingTemplate = true;
      this.error = "";

      try {
        const response = await downloadMembersTemplate();

        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "application/octet-stream",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "members_import_template.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = e?.response?.data?.message || "Failed to download template.";
      } finally {
        this.downloadingTemplate = false;
      }
    },

    async submit() {
      if (!this.file) {
        this.error = "Please choose a file to import.";
        return;
      }

      this.submitting = true;
      this.error = "";
      this.successMessage = "";
      this.result = null;

      try {
        const formData = new FormData();
        formData.append("file", this.file);

        const response = await importMembers(formData);
        this.result = response.data?.result || null;
        this.successMessage = response.data?.message || "Import completed successfully.";

        this.$emit("imported");
      } catch (e) {
        this.error = e?.response?.data?.message || "Import failed.";
        this.result = e?.response?.data?.result || null;
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>